import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { ApiError } from '../errors.js';
import { loadConfig } from '../config.js';
import { isAuthenticated } from '../auth.js';
import { resolveActiveOrg, activeOrgSourceLabel, type ActiveOrg } from '../active-org.js';
import { readBinding } from '../base-binding.js';
import { getFormat } from '../helpers.js';
import { nudgeReportActions } from '../report-inbox.js';
import type { MeResponse } from './whoami.js';
import pkg from '../../package.json' with { type: 'json' };

interface StatusSnapshot {
  logged_in: boolean;
  token_valid: boolean | null;
  api_url: string;
  auth_kind: string | null;
  email: string | null;
  org_id: string | null;
  active_org: string | null;
  active_org_source: ActiveOrg['source'] | null;
  worktree_binding: string | null;
  grants: string | null;
  cli: { version: string };
}

export const statusCommand = new Command('status')
  .description('Show auth, connectivity, and CLI version diagnostics')
  .action(async function (this: Command) {
    const config = loadConfig();
    const loggedIn = await isAuthenticated();

    // null means "not determined" (never set false just because no credential exists) —
    // it flips to false only on a real 401/403 rejection, avoiding a misleading verdict.
    let tokenValid: boolean | null = null;
    let me: MeResponse | null = null;
    if (loggedIn) {
      try {
        me = await new ApiClient().request<MeResponse>('GET', '/v1/auth/me');
        tokenValid = true;
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) tokenValid = false;
      }
    }

    const active = resolveActiveOrg();
    const worktreeBinding = readBinding();

    const snapshot: StatusSnapshot = {
      logged_in: loggedIn,
      token_valid: tokenValid,
      api_url: config.api_url,
      auth_kind: me?.auth_kind ?? null,
      email: me?.email ?? null,
      org_id: me?.org_id ?? null,
      active_org: active.orgId ?? null,
      active_org_source: active.source ?? null,
      worktree_binding: worktreeBinding,
      grants: me?.grants_summary ?? null,
      cli: { version: pkg.version },
    };

    if (getFormat(this) === 'json') {
      console.log(JSON.stringify(snapshot, null, 2));
      return;
    }

    if (!loggedIn) {
      console.log('Not logged in. Run `rekor login`.');
      console.log(`API: ${config.api_url}`);
      return;
    }

    const tokenStatus =
      tokenValid === true
        ? 'token valid'
        : tokenValid === false
          ? 'token rejected — run `rekor login` to re-authenticate'
          : 'token validity unknown — backend unreachable';
    console.log(`Logged in${me?.email ? ` as ${me.email}` : ''} — ${tokenStatus}`);
    console.log(`API: ${config.api_url}`);
    if (me?.auth_kind) console.log(`Auth: ${me.auth_kind}`);
    if (me?.org_id) console.log(`Org: ${me.org_id}`);
    if (active.orgId && active.source) {
      console.log(`Active org: ${active.orgId} (from ${activeOrgSourceLabel(active.source)})`);
    }
    if (worktreeBinding) console.log(`Worktree bound to database: ${worktreeBinding} (run \`rekor unbind\` to clear)`);
    if (me?.grants_summary) console.log(`Grants: ${me.grants_summary}`);

    // Best-effort, non-JSON path only (the --json branch returned above): remind the reporter of any
    // reports awaiting their accept/contest. Self-guards on TTY/CI; only runs with a valid token.
    if (tokenValid === true) {
      await nudgeReportActions();
    }
  });
